/**
 * DECAY SYSTEM - Sistema de Deterioro Automático
 * Genera daños periódicos en la casa
 */

import { DECAY_CONFIG, COMPONENTS, DAMAGE_TYPES } from "../utils/constants.js";
import { randomElement } from "../utils/helpers.js";

export default class DecaySystem {
  constructor(scene, interval = DECAY_CONFIG.INITIAL_INTERVAL) {
    this.scene = scene;
    this.interval = interval; // segundos entre daños
    this.isRunning = false;
    this.decayEvent = null;
  }

  /**
   * Iniciar el sistema de decay
   */
  start() {
    if (this.isRunning) return;

    this.isRunning = true;

    // Crear evento de decay que se ejecuta periódicamente
    this.decayEvent = this.scene.time.addEvent({
      delay: this.interval * 1000, // convertir a milisegundos
      callback: this.spawnDamage,
      callbackScope: this,
      loop: true,
    });

    this.onDecayStarted();
  }

  /**
   * Pausar el decay
   */
  pause() {
    if (this.decayEvent) {
      this.decayEvent.paused = true;
    }
    console.log("⏸ Decay pausado");
  }

  /**
   * Reanudar el decay
   */
  resume() {
    if (this.decayEvent) {
      this.decayEvent.paused = false;
    }
    console.log("▶️ Decay reanudado");
  }

  /**
   * Detener el sistema de decay
   */
  stop() {
    if (!this.isRunning) return;

    this.isRunning = false;

    if (this.decayEvent) {
      this.decayEvent.remove();
      this.decayEvent = null;
    }

    this.onDecayStopped();
  }

  /**
   * Generar daño en un componente aleatorio
   */
  spawnDamage() {
    // Seleccionar componente aleatorio
    const componentKeys = Object.keys(COMPONENTS);
    const randomKey = randomElement(componentKeys);
    const component = COMPONENTS[randomKey];

    // Seleccionar tipo de daño apropiado
    const damageType = this.selectDamageType(component);

    // Emitir evento para que la escena cree el daño visual
    this.scene.events.emit("decayDamageSpawned", {
      component: component,
      damageType: damageType,
    });

    this.onDamageSpawned(component, damageType);
  }

  /**
   * Seleccionar tipo de daño según el componente
   * @param {object} component - Componente afectado
   * @returns {object} Tipo de daño
   */
  selectDamageType(component) {
    // Componentes críticos (TSE, Constitución) → más grietas
    if (component.critical) {
      return DAMAGE_TYPES.CRACK;
    }

    // Municipalidades → más vines (puntos de acceso)
    if (component.id === "municipalidades") {
      return Math.random() > 0.3 ? DAMAGE_TYPES.VINE : DAMAGE_TYPES.CRACK;
    }

    // Otros componentes → balance 50/50
    return Math.random() > 0.5 ? DAMAGE_TYPES.CRACK : DAMAGE_TYPES.VINE;
  }

  /**
   * Aumentar dificultad (reducir intervalo)
   * @param {number} decreaseAmount - Cuánto reducir (en segundos)
   */
  increaseDifficulty(decreaseAmount = DECAY_CONFIG.DECREASE_PER_ROUND) {
    this.interval = Math.max(
      this.interval - decreaseAmount,
      DECAY_CONFIG.MIN_INTERVAL,
    );

    // Si está corriendo, reiniciar con nuevo intervalo
    if (this.isRunning) {
      this.stop();
      this.start();
    }

    this.onDifficultyIncreased();
  }

  /**
   * Establecer intervalo específico
   * @param {number} newInterval - Nuevo intervalo en segundos
   */
  setInterval(newInterval) {
    this.interval = newInterval;

    // Si está corriendo, reiniciar con nuevo intervalo
    if (this.isRunning) {
      this.stop();
      this.start();
    }
  }

  /**
   * Obtener intervalo actual
   * @returns {number} Intervalo en segundos
   */
  getInterval() {
    return this.interval;
  }

  /**
   * Resetear a intervalo inicial
   */
  reset(newInterval = DECAY_CONFIG.INITIAL_INTERVAL) {
    this.stop();
    this.interval = newInterval;
  }

  /**
   * Callback cuando inicia el decay
   */
  onDecayStarted() {
    console.log(`🌿 Decay iniciado - Intervalo: ${this.interval}s`);
  }

  /**
   * Callback cuando se detiene el decay
   */
  onDecayStopped() {
    console.log("🌿 Decay detenido");
  }

  /**
   * Callback cuando se genera un daño
   */
  onDamageSpawned(component, damageType) {
    console.log(`🌿 Daño generado: ${damageType.name} en ${component.name}`);
  }

  /**
   * Callback cuando aumenta la dificultad
   */
  onDifficultyIncreased() {
    console.log(`🌿 Dificultad aumentada - Nuevo intervalo: ${this.interval}s`);
  }

  /**
   * Destruir el sistema
   */
  destroy() {
    this.stop();
    this.scene = null;
  }
}
